/**
 * Helpers the block declarations share: the sample rows of examples and small builders for the example nodes
 * (a heading, a text, a stat …), so every example stays short and reads the same way in the palette, the catalog
 * and the tests. Pure data.
 */
import { type ExampleNode, L, type Lbl, type StaticRow } from './custom-blocks';

type Json = Record<string, unknown>;

const t = (en: string, de: string): Lbl => L(en, de);

export const ex = {
  heading: (en: string, de: string, level: 1 | 2 | 3 = 3): ExampleNode => ({ type: 'heading', props: { text: t(en, de), level } }),
  text: (en: string, de: string, props: Json = {}): ExampleNode => ({ type: 'text', props: { text: t(en, de), ...props } }),
  muted: (en: string, de: string): ExampleNode => ({ type: 'text', props: { text: t(en, de), tone: 'muted', size: 'sm' } }),
  badge: (en: string, de: string, tone = 'neutral'): ExampleNode => ({ type: 'badge', props: { text: t(en, de), tone } }),
  button: (en: string, de: string, variant = 'secondary'): ExampleNode => ({ type: 'button', props: { label: t(en, de), variant, call: { action: 'notify', args: {} } } }),
  stat: (en: string, de: string, value: string, hint?: Lbl): ExampleNode => ({ type: 'stat', props: { label: t(en, de), value, ...(hint ? { hint } : {}) } }),
  progress: (en: string, de: string, value: number, max = 100): ExampleNode => ({ type: 'progress', props: { label: t(en, de), value: String(value), max: String(max) } }),
  stack: (children: ExampleNode[], props: Json = {}): ExampleNode => ({ type: 'stack', props: { direction: 'column', gap: 'sm', ...props }, children }),
  row: (children: ExampleNode[], props: Json = {}): ExampleNode => ({ type: 'stack', props: { direction: 'row', gap: 'sm', align: 'center', ...props }, children }),
  panel: (en: string, de: string, children: ExampleNode[], props: Json = {}): ExampleNode => ({ type: 'panel', props: { label: t(en, de), ...props }, children }),
  card: (en: string, de: string, children: ExampleNode[]): ExampleNode => ({ type: 'card', props: { title: t(en, de) }, children }),
};

// -- sample rows ------------------------------------------------------------------------------------------

type Field = { key: string; type: 'text' | 'number' | 'date' | 'boolean' };
export type SampleData = { id: string; fields: Field[]; rows: StaticRow[] };

/** People with a role, a team and a load: tables, boards, leaderboards, org charts, avatars. */
export const PEOPLE: SampleData = {
  id: 'sample',
  fields: [
    { key: 'title', type: 'text' },
    { key: 'role', type: 'text' },
    { key: 'team', type: 'text' },
    { key: 'status', type: 'text' },
    { key: 'score', type: 'number' },
    { key: 'manager', type: 'text' },
  ],
  rows: [
    { id: 'p1', title: 'Mara Klein', role: 'Head of Operations', team: 'Operations', status: 'active', score: 92, manager: '' },
    { id: 'p2', title: 'Tim Bauer', role: 'Account Manager', team: 'Support', status: 'active', score: 78, manager: 'Mara Klein' },
    { id: 'p3', title: 'Robert Pohl', role: 'Administrator', team: 'Operations', status: 'away', score: 64, manager: 'Mara Klein' },
    { id: 'p4', title: 'Lena Fuchs', role: 'Designer', team: 'Product', status: 'active', score: 85, manager: 'Mara Klein' },
    { id: 'p5', title: 'Jonas Weber', role: 'Engineer', team: 'Product', status: 'busy', score: 71, manager: 'Lena Fuchs' },
    { id: 'p6', title: 'Ayla Demir', role: 'Engineer', team: 'Product', status: 'active', score: 58, manager: 'Lena Fuchs' },
  ],
};

/** Work items with a state, an owner, a due date and a start/end: boards, timelines, gantt, calendars, checklists. */
export const WORK: SampleData = {
  id: 'sample',
  fields: [
    { key: 'title', type: 'text' },
    { key: 'status', type: 'text' },
    { key: 'owner', type: 'text' },
    { key: 'start', type: 'date' },
    { key: 'end', type: 'date' },
    { key: 'progress', type: 'number' },
    { key: 'done', type: 'boolean' },
    { key: 'priority', type: 'text' },
    { key: 'parent', type: 'text' },
    { key: 'note', type: 'text' },
  ],
  rows: [
    { id: 'w1', title: 'Kick-off and scope', status: 'done', owner: 'Mara Klein', start: '2026-10-05', end: '2026-10-07', progress: 100, done: true, priority: 'high', parent: '', note: 'Goals agreed with the team' },
    { id: 'w2', title: 'Design review', status: 'doing', owner: 'Lena Fuchs', start: '2026-10-08', end: '2026-10-14', progress: 60, done: false, priority: 'high', parent: '', note: 'Two rounds planned' },
    { id: 'w3', title: 'Build the first version', status: 'doing', owner: 'Jonas Weber', start: '2026-10-12', end: '2026-10-23', progress: 35, done: false, priority: 'normal', parent: 'w2', note: 'Behind a feature flag' },
    { id: 'w4', title: 'Partner pilot', status: 'todo', owner: 'Tim Bauer', start: '2026-10-26', end: '2026-11-06', progress: 0, done: false, priority: 'normal', parent: '', note: 'Three accounts' },
    { id: 'w5', title: 'Launch', status: 'todo', owner: 'Mara Klein', start: '2026-11-09', end: '2026-11-10', progress: 0, done: false, priority: 'low', parent: 'w4', note: 'Announcement and handover' },
  ],
};

/** Numbers over time: sparklines, heat calendars, gauges. */
export const SERIES: SampleData = {
  id: 'sample',
  fields: [
    { key: 'title', type: 'text' },
    { key: 'date', type: 'date' },
    { key: 'value', type: 'number' },
    { key: 'group', type: 'text' },
  ],
  rows: Array.from({ length: 28 }, (_, i) => ({
    id: `s${i + 1}`,
    title: `Day ${i + 1}`,
    date: `2026-09-${String(i + 1).padStart(2, '0')}`,
    value: Math.round(40 + 30 * Math.sin(i / 2.6) + (i % 5) * 6),
    group: i % 2 ? 'B' : 'A',
  })),
};

/** Categories with a value: distribution bars, leaderboards, funnels in simple form. */
export const SPLIT: SampleData = {
  id: 'sample',
  fields: [
    { key: 'title', type: 'text' },
    { key: 'value', type: 'number' },
    { key: 'tone', type: 'text' },
  ],
  rows: [
    { id: 'c1', title: 'Done', value: 48, tone: 'success' },
    { id: 'c2', title: 'In progress', value: 27, tone: 'accent' },
    { id: 'c3', title: 'Blocked', value: 9, tone: 'danger' },
    { id: 'c4', title: 'Not started', value: 16, tone: 'neutral' },
  ],
};

/** Short texts with a date and an author: feeds, comments, timelines. */
export const POSTS: SampleData = {
  id: 'sample',
  fields: [
    { key: 'title', type: 'text' },
    { key: 'body', type: 'text' },
    { key: 'author', type: 'text' },
    { key: 'date', type: 'date' },
    { key: 'kind', type: 'text' },
  ],
  rows: [
    { id: 'f1', title: 'Quote sent', body: 'The offer went out to the partner.', author: 'Tim Bauer', date: '2026-10-09', kind: 'mail' },
    { id: 'f2', title: 'Call with the buyer', body: 'They want a pilot before the end of the quarter.', author: 'Mara Klein', date: '2026-10-08', kind: 'call' },
    { id: 'f3', title: 'Contract signed', body: 'Signed by both parties.', author: 'Robert Pohl', date: '2026-10-07', kind: 'success' },
    { id: 'f4', title: 'Note', body: 'Follow up next Tuesday.', author: 'Tim Bauer', date: '2026-10-06', kind: 'note' },
  ],
};

/** A people-by-week load table: heat maps and capacity planning. */
export const LOAD: SampleData = {
  id: 'sample',
  fields: [
    { key: 'title', type: 'text' },
    { key: 'person', type: 'text' },
    { key: 'week', type: 'text' },
    { key: 'hours', type: 'number' },
  ],
  rows: ['Mara', 'Tim', 'Lena', 'Jonas'].flatMap((person, pi) =>
    ['W41', 'W42', 'W43', 'W44', 'W45'].map((week, wi) => ({
      id: `l${pi}${wi}`,
      title: `${person} ${week}`,
      person,
      week,
      hours: [18, 24, 32, 38, 44, 28, 36, 12][(pi * 3 + wi * 2) % 8]!,
    })),
  ),
};

export const sampleRef = (data: SampleData) => ({ data: [data] });

/** Appointments with a start and an end time (a week or day schedule). */
export const MEETINGS: SampleData = {
  id: 'sample',
  fields: [
    { key: 'title', type: 'text' },
    { key: 'start', type: 'text' },
    { key: 'end', type: 'text' },
    { key: 'status', type: 'text' },
  ],
  rows: [
    { id: 'm1', title: 'Team stand-up', start: '2026-10-12T08:30:00+02:00', end: '2026-10-12T09:00:00+02:00', status: 'accent' },
    { id: 'm2', title: 'Design review', start: '2026-10-12T10:00:00+02:00', end: '2026-10-12T11:30:00+02:00', status: 'warning' },
    { id: 'm3', title: 'Partner call', start: '2026-10-13T14:00:00+02:00', end: '2026-10-13T15:00:00+02:00', status: 'success' },
    { id: 'm4', title: 'Planning', start: '2026-10-14T09:00:00+02:00', end: '2026-10-14T12:00:00+02:00', status: 'accent' },
    { id: 'm5', title: 'One to one', start: '2026-10-14T11:00:00+02:00', end: '2026-10-14T11:45:00+02:00', status: 'neutral' },
    { id: 'm6', title: 'Release check', start: '2026-10-15T16:00:00+02:00', end: '2026-10-15T17:00:00+02:00', status: 'danger' },
  ],
};

/** The one action examples with buttons call: it only shows a note. */
export const NOTIFY = {
  id: 'notify',
  name: t('Show a note', 'Hinweis zeigen'),
  steps: [{ kind: 'message', text: t('Done', 'Erledigt'), tone: 'success' }],
};
